import type { CSSProperties } from 'react';
import config from '@config';

// Baked at build time by vite.config's `define`. Guard the typeof so a context where
// the define somehow didn't run can't throw a ReferenceError.
declare const __APP_VERSION__: string;
declare const __BUILD_SHA__: string;

const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0';
const sha = typeof __BUILD_SHA__ === 'string' ? __BUILD_SHA__ : 'dev';

type BuildConfig = { appEnv?: string; prId?: string | number | null };

// Prod shows just version + sha; every other env is named so preview/test builds are
// obvious. Preview also carries its PR number.
function envSuffix(): string {
  const { appEnv, prId } = config as BuildConfig;
  const env = String(appEnv || '').toLowerCase();
  if (env === 'production' || env === 'prod') return '';
  if (env === 'preview') return prId ? ` · preview ${prId}` : ' · preview';
  if (!env) return '';
  return ` · ${env}`;
}

/** e.g. `v1.4.2 · ab12cd3 · preview 579`. */
export function buildIdentity(): string {
  return `v${version} · ${sha}${envSuffix()}`;
}

/** Just the primary version part, e.g. `v1.4.2` — for tight spots like the collapsed rail. */
export function shortVersion(): string {
  return `v${version}`;
}

const baseStyle: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  lineHeight: 1.4,
  letterSpacing: '0.02em',
  color: '#9aa0a6',
  userSelect: 'none',
};

const fixedStyle: CSSProperties = {
  position: 'fixed',
  right: 10,
  bottom: 8,
  zIndex: 2147483000,
  pointerEvents: 'none',
  opacity: 0.7,
};

/**
 * Subdued build-identity marker (version, short sha, env) so anyone can tell which
 * build is live. `fixed` pins it bottom-right for landing/full-screen surfaces;
 * inline (default) drops it into an existing footer or nav.
 */
export function BuildBadge({ fixed = false, style }: { fixed?: boolean; style?: CSSProperties }) {
  return <span style={{ ...baseStyle, ...(fixed ? fixedStyle : {}), ...style }}>{buildIdentity()}</span>;
}

export default BuildBadge;
